import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Gift, Sparkles, ChevronRight, Clock, Loader2, Camera } from "lucide-react";
import { resolveImageUrl } from "../components/ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public bundle page — a manager sends this URL via WhatsApp and the
 * recipient (potential client) opens it WITHOUT logging in. They see:
 *
 *   • Manager card at the top (personal touch)
 *   • 5 full product cards with photo, price, sizes, description
 *   • Sticky CTA banner promising 10.000 ₸ welcome bonus on registration
 *
 * Clicking any product or the CTA lands on `/app/auth?share=<token>` so
 * that after signup the visitor is auto-assigned to this manager in CRM.
 *
 * URL: `/app/share/:token` (see App.js routes).
 */
export default function PublicShare() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    axios.get(`${API}/shares/${token}`)
      .then(r => setState({ loading: false, error: "", data: r.data }))
      .catch(e => setState({
        loading: false,
        error: e.response?.data?.detail || "Ссылка не найдена или устарела",
        data: null,
      }));
  }, [token]);

  const goSignup = (intent = null) => {
    // Persist the share token so the OTP register flow can consume it —
    // ProductDetail / Auth reads it back from sessionStorage.
    try {
      sessionStorage.setItem("share_token", token);
      // If the visitor came in via the "Try-On" CTA we route them straight
      // to the AR fitting page after signup instead of the home feed.
      if (intent) sessionStorage.setItem("post_signup_intent", intent);
      else sessionStorage.removeItem("post_signup_intent");
    } catch { /* noop */ }
    // Fire-and-forget click event so the manager sees the intent live.
    axios.post(`${API}/shares/${token}/click`,
                { kind: intent || "login" }).catch(() => {});
    const q = intent ? `&intent=${intent}` : "";
    navigate(`/auth?share=${encodeURIComponent(token)}${q}`);
  };

  const openProduct = (productId) => {
    axios.post(`${API}/shares/${token}/click`,
                { kind: "product", product_id: productId }).catch(() => {});
    goSignup();
  };

  if (state.loading) {
    return (
      <div style={centerScreen}>
        <Loader2 size={28} className="animate-spin" style={{ color: "var(--ang-gold)" }} />
      </div>
    );
  }
  if (state.error) {
    return (
      <div style={{ ...centerScreen, flexDirection: "column", gap: 14 }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "var(--ang-bg)" }}>
          Angiolini
        </div>
        <div style={{ fontFamily: "Manrope", color: "#7a7a79", fontSize: 14, maxWidth: 320, textAlign: "center" }}>
          {state.error}
        </div>
        <button data-testid="share-back-home" onClick={() => navigate("/auth")}
          style={btnPrimary}>Открыть Angiolini</button>
      </div>
    );
  }

  const { manager, products, expires_at } = state.data;
  const expiresIn = expires_at ? timeLeft(new Date(expires_at)) : null;

  return (
    <div data-testid="public-share-page"
      style={{ minHeight: "100vh", background: "#faf9f7", paddingBottom: 100 }}>
      {/* Elegant hero */}
      <div style={{
        background: "linear-gradient(180deg, var(--ang-bg) 0%, var(--ang-surface) 100%)",
        color: "var(--ang-text)", padding: "36px 20px 30px", textAlign: "center",
      }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 36, fontWeight: 400, letterSpacing: 4 }}>
          ANGIOLINI
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 10, color: "var(--ang-text-muted)", letterSpacing: 3, marginTop: 4 }}>
          ITALIAN FUR COUTURE
        </div>

        {manager && (
          <div style={{ marginTop: 22,
                        display: "inline-flex", alignItems: "center", gap: 10,
                        background: "var(--ang-gold-soft)", padding: "8px 16px",
                        borderRadius: 999, border: "1px solid var(--ang-gold-line)" }}>
            <Sparkles size={14} style={{ color: "var(--ang-gold-2)" }} />
            <span style={{ fontFamily: "Manrope", fontSize: 12, color: "var(--ang-gold-2)", fontWeight: 600 }}>
              Персональная подборка от {manager.name}
            </span>
          </div>
        )}
        {expiresIn && (
          <div style={{ marginTop: 8, fontFamily: "Manrope", fontSize: 11,
                        color: "var(--ang-text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Clock size={11} /> ссылка активна {expiresIn}
          </div>
        )}
      </div>

      {/* Products */}
      <div style={{ padding: "24px 16px 20px", maxWidth: 760, margin: "0 auto" }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "var(--ang-bg)",
                      marginBottom: 6, textAlign: "center" }}>
          Выбрано специально для вас
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 12, color: "var(--ang-text-muted)",
                      textAlign: "center", marginBottom: 22 }}>
          {products.length} {productWord(products.length)} · {formatPriceRange(products)}
        </div>

        {products.map((p) => (
          <ProductCard key={p.id} p={p} onOpen={() => openProduct(p.id)} />
        ))}
      </div>

      {/* Sticky CTA */}
      <div data-testid="share-cta"
        style={{ position: "fixed", left: 0, right: 0, bottom: 0,
                 background: "linear-gradient(180deg, rgba(250,249,247,0) 0%, #faf9f7 20%)",
                 padding: "12px 16px 16px", zIndex: 50,
                 borderTop: "1px solid var(--ang-gold-soft)" }}>
        <button data-testid="share-cta-tryon" onClick={() => goSignup("tryon")}
          style={{
            width: "100%", maxWidth: 480, margin: "0 auto 10px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            background: "var(--ang-bg)",
            border: "1px solid var(--ang-gold)", borderRadius: 8, padding: "13px 20px",
            color: "var(--ang-gold-2)", fontFamily: "Manrope", fontSize: 13.5, fontWeight: 700,
            letterSpacing: 0.3, cursor: "pointer",
          }}>
          <Camera size={17} />
          Примерить онлайн
        </button>
        <button data-testid="share-cta-signup" onClick={() => goSignup()}
          style={{
            width: "100%", maxWidth: 480, margin: "0 auto",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            background: "linear-gradient(135deg, var(--ang-gold) 0%, var(--ang-gold-2) 100%)",
            border: "none", borderRadius: 8, padding: "14px 20px",
            color: "var(--ang-bg)", fontFamily: "Manrope", fontSize: 14, fontWeight: 700,
            cursor: "pointer", boxShadow: "0 6px 20px var(--ang-gold-line)",
          }}>
          <Gift size={18} />
          Зарегистрироваться и получить 10 000 ₸
        </button>
        <div style={{ textAlign: "center", fontFamily: "Manrope", fontSize: 10,
                      color: "var(--ang-text-muted)", marginTop: 6 }}>
          Виртуальная примерка, размеры, доставка — доступно после регистрации
        </div>
      </div>
    </div>
  );
}

function ProductCard({ p, onOpen }) {
  const img = (p.images || [])[0];
  const price = Number(p.price || 0).toLocaleString("ru-RU");
  const oldP = p.old_price ? Number(p.old_price).toLocaleString("ru-RU") : null;
  const desc = (p.description || "").slice(0, 220);

  return (
    <div data-testid={`share-product-${p.id}`}
      onClick={onOpen}
      style={{ background: "white", borderRadius: 12, marginBottom: 16,
               overflow: "hidden", boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
               border: "1px solid #f1f1ee", cursor: "pointer",
               transition: "transform .2s, box-shadow .2s" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(15,23,42,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 2px 12px rgba(15,23,42,0.06)";
      }}>
      {img ? (
        <img src={resolveImageUrl(img)} alt={p.name}
          style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", background: "#f5f5f4" }} />
      ) : (
        <div style={{ width: "100%", aspectRatio: "3/4", background: "#f5f5f4",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "Manrope", fontSize: 12, color: "var(--ang-text-muted)" }}>
          нет фото
        </div>
      )}
      <div style={{ padding: "14px 16px 16px" }}>
        {p.brand && (
          <div style={{ fontFamily: "Manrope", fontSize: 10, color: "var(--ang-text-muted)",
                        letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
            {p.brand}
          </div>
        )}
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 20, fontWeight: 500,
                      color: "var(--ang-bg)", lineHeight: 1.25, marginBottom: 8 }}>
          {p.name}
        </div>
        {desc && (
          <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#5a5a59",
                        lineHeight: 1.55, marginBottom: 10 }}>
            {desc}{p.description?.length > 220 ? "…" : ""}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
          <span style={{ fontFamily: "Cormorant Garamond", fontSize: 22, fontWeight: 600, color: "var(--ang-gold)" }}>
            {price} ₸
          </span>
          {oldP && (
            <span style={{ fontFamily: "Manrope", fontSize: 12, color: "var(--ang-text-muted)",
                           textDecoration: "line-through" }}>
              {oldP} ₸
            </span>
          )}
        </div>
        {p.sizes?.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {p.sizes.slice(0, 8).map(s => (
              <span key={s} style={{ padding: "3px 8px", border: "1px solid #e5e7eb",
                                      borderRadius: 4, fontFamily: "Manrope", fontSize: 11,
                                      color: "#374151" }}>{s}</span>
            ))}
          </div>
        )}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "Manrope",
                      fontSize: 12, color: "var(--ang-gold)", fontWeight: 600 }}>
          Открыть карточку <ChevronRight size={14} />
        </div>
      </div>
    </div>
  );
}

// --------------- helpers ---------------

const centerScreen = { minHeight: "100vh", display: "flex", alignItems: "center",
                       justifyContent: "center", background: "#faf9f7" };
const btnPrimary = {
  background: "linear-gradient(135deg, var(--ang-gold), var(--ang-gold-2))", color: "var(--ang-bg)",
  border: "none", borderRadius: 8, padding: "10px 18px",
  fontFamily: "Manrope", fontSize: 13, fontWeight: 700, cursor: "pointer",
};

function productWord(n) {
  const rem10 = n % 10, rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 14) return "моделей";
  if (rem10 === 1) return "модель";
  if (rem10 >= 2 && rem10 <= 4) return "модели";
  return "моделей";
}

function formatPriceRange(products) {
  if (!products.length) return "";
  const prices = products.map(p => Number(p.price || 0));
  const min = Math.min(...prices), max = Math.max(...prices);
  if (min === max) return `${min.toLocaleString("ru-RU")} ₸`;
  return `${min.toLocaleString("ru-RU")} – ${max.toLocaleString("ru-RU")} ₸`;
}

function timeLeft(expiresAt) {
  const ms = expiresAt.getTime() - Date.now();
  if (ms <= 0) return "истекла";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return `ещё ${d} дн ${h ? h + " ч" : ""}`.trim();
  if (h >= 1) return `ещё ${h} ч ${m ? m + " мин" : ""}`.trim();
  return `ещё ${m} мин`;
}
